import BaseService from "./base.service";
import ApiService from "./api.service";

// Editor de campanha v2 (beta): uma chamada por aba, sob demanda.
// Backend: CampaignEditorController (api/CampaignEditor/...).

export const HTTP_CONFLICT_STATUS = 409;

// Listas estáticas pequenas: uma requisição por sessão da SPA (o back também tem cache).
let _lookupsPromise = null;

export default class CampaignEditorService extends BaseService {
	constructor() {
		super("CampaignEditor");
	}

	getHeader(campaignId) {
		return this.getRequest("Header", { campaignId }, true, false);
	}

	getLookups() {
		if (!_lookupsPromise) {
			_lookupsPromise = this.getRequest("Lookups", null, true, false)
				.then((data) => deepFreeze(data))
				.catch((error) => {
					_lookupsPromise = null;
					throw error;
				});
		}
		return _lookupsPromise;
	}

	getGeneral(campaignId) {
		return this.getRequest("General", { campaignId }, true, false);
	}

	saveGeneral(model) {
		return this.saveSection("General", model);
	}

	getSharing(campaignId) {
		return this.getRequest("Sharing", { campaignId }, true, false);
	}

	saveSharing(model) {
		return this.saveSection("Sharing", model);
	}

	getCheckout(campaignId) {
		return this.getRequest("Checkout", { campaignId }, true, false);
	}

	saveCheckout(model) {
		return this.saveSection("Checkout", model);
	}

	listProjects(campaignId, page, pageSize, search) {
		return this.getRequest("Projects", { campaignId, page, pageSize, search: search || null }, true, false);
	}

	searchProjects(campaignId, search, page) {
		return this.getRequest("ProjectSearch", { campaignId, search: search || null, page }, true, false);
	}

	// --- Pagamentos e taxas / Termos e LGPD / Sorteio / Integrações (PUT com versão e 409) ---
	getPayments(campaignId) {
		return this.getRequest("Payments", { campaignId }, true, false);
	}

	savePayments(model) {
		return this.saveSection("Payments", model);
	}

	getTerms(campaignId) {
		return this.getRequest("Terms", { campaignId }, true, false);
	}

	saveTerms(model) {
		return this.saveSection("Terms", model);
	}

	getDraw(campaignId) {
		return this.getRequest("Draw", { campaignId }, true, false);
	}

	saveDraw(model) {
		return this.saveSection("Draw", model);
	}

	getIntegrations(campaignId) {
		return this.getRequest("Integrations", { campaignId }, true, false);
	}

	saveIntegrations(model) {
		return this.saveSection("Integrations", model);
	}

	// --- Produtos e brindes (listagem + ativar/inativar; criar/editar usa os modais existentes) ---
	getProducts(campaignId) {
		return this.getRequest("Products", { campaignId }, true, false);
	}

	setProductStatus(campaignId, itemId, active) {
		return this.putRequest("ProductStatus", { campaignId, itemId, active }, true, false);
	}

	setGiftStatus(campaignId, itemId, active) {
		return this.putRequest("GiftStatus", { campaignId, itemId, active }, true, false);
	}

	// --- Projetos: operações parciais ---
	getSupporterOptions(campaignId) {
		return this.getRequest("SupporterOptions", { campaignId }, true, false);
	}

	addProject(campaignId, projectId) {
		return this.postRequest("ProjectAdd", { campaignId, projectId });
	}

	removeProject(campaignId, projectId) {
		return this.deleteRequest("ProjectRemove", { campaignId, projectId });
	}

	setProjectSupporters(campaignId, projectId, companyIds) {
		return this.putRequest("ProjectSupporters", { campaignId, projectId, companyIds });
	}

	/**
	 * PUT de uma aba. O 409 (campanha alterada por outra pessoa/aba) é devolvido como
	 * erro com `conflict: true` para a aba exibir a mensagem e oferecer recarregar —
	 * os demais erros seguem o tratamento padrão do BaseService (swal / login).
	 */
	saveSection(action, model) {
		this.showLoader();
		return ApiService.put(this.controllerName + action, model)
			.then((response) => this.handleReturn(response, true, true))
			.catch((error) => {
				const response = error && error.response;
				if (response && response.status === HTTP_CONFLICT_STATUS) {
					this.hideLoader();
					const message = (response.data && response.data.errorMessage) ||
						"Esta campanha foi alterada por outra pessoa. Recarregue a aba antes de salvar.";
					throw { conflict: true, errorMessage: message };
				}
				if (error && error.errorMessage !== undefined) throw error;
				return this.handleReturn(error, true, true);
			});
	}
}

function deepFreeze(value) {
	if (value && typeof value === "object" && !Object.isFrozen(value)) {
		Object.freeze(value);
		Object.keys(value).forEach((key) => deepFreeze(value[key]));
	}
	return value;
}
